import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Modal } from "app/components/Modal";
import { DropdownIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  BODY_TYPE_OPTIONS,
  DH_TYPE_OPTIONS,
  DRIVER_CABIN_TYPE_OPTIONS,
  RegistrationSelectOption,
  SUSPENSION_TYPE_OPTIONS,
  TIRE_OPTIONS,
} from "../../data";
import {
  selectIsAddModalOpen,
  selectRegistrationForm,
  selectRegistrationSaving,
} from "../../redux/selector";
import { registrationActions } from "../../redux/slice";
import { RegistrationForm } from "../../types";
import { useStyles } from "./styles";

type FieldConfig = {
  key: keyof RegistrationForm;
  label: string;
  type: "select" | "text" | "textarea";
  options?: RegistrationSelectOption[];
  fullWidth?: boolean;
};

const ROWS: FieldConfig[][] = [
  [
    {
      key: "driverCabinType",
      label: "DRIVER_CABIN_TYPE",
      type: "select",
      options: DRIVER_CABIN_TYPE_OPTIONS,
    },
    {
      key: "tire",
      label: "TIRE",
      type: "select",
      options: TIRE_OPTIONS,
    },
  ],
  [
    {
      key: "suspensionType",
      label: "SUSPENSION_TYPE",
      type: "select",
      options: SUSPENSION_TYPE_OPTIONS,
    },
    { key: "weight", label: "WEIGHT", type: "text" },
  ],
  [
    { key: "axlesMeasure", label: "AXLES_MEASURE", type: "text" },
    { key: "lowerLimit", label: "LOWER_LIMIT", type: "text" },
    { key: "upperLimit", label: "UPPER_LIMIT", type: "text" },
  ],
  [
    { key: "frontLoad", label: "FRONT_LOAD", type: "text" },
    { key: "rearAxlesMeasure", label: "REAR_AXLES_MEASURE", type: "text" },
  ],
  [{ key: "notes", label: "NOTES", type: "textarea", fullWidth: true }],
  [
    {
      key: "dhType",
      label: "DH_TYPE",
      type: "select",
      options: DH_TYPE_OPTIONS,
    },
    {
      key: "bodyType",
      label: "BODY_TYPE",
      type: "select",
      options: BODY_TYPE_OPTIONS,
    },
  ],
];

const REQUIRED_FIELDS: Array<keyof RegistrationForm> = [
  "driverCabinType",
  "tire",
  "suspensionType",
  "weight",
  "axlesMeasure",
  "lowerLimit",
  "upperLimit",
  "frontLoad",
  "rearAxlesMeasure",
  "dhType",
  "bodyType",
];

const P = "REGISTRATION.MODAL";

const AddRegistrationModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectRegistrationForm);
  const saving = useSelector(selectRegistrationSaving);

  const onClose = () => {
    if (saving) return;
    dispatch(registrationActions.closeAddModal());
  };

  const setField = (name: keyof RegistrationForm, value: string) =>
    dispatch(registrationActions.setFormField({ name, value }));

  const placeholder = (label: string, type: "select" | "text" | "textarea") =>
    t(
      `${P}.PLACEHOLDERS.${type === "select" ? "SELECT" : "ENTER"}_${label}`
    );

  const isValid = REQUIRED_FIELDS.every((key) => Boolean(form[key].trim()));

  const optionLabel = (option: RegistrationSelectOption) =>
    option.labelKey ? t(option.labelKey) : option.label || option.value;

  const renderField = ({
    key,
    label,
    type,
    options = [],
    fullWidth,
  }: FieldConfig) => (
    <Box
      key={key}
      className={`${styles.field}${fullWidth ? ` ${styles.fullWidth}` : ""}`}
    >
      <Typography className={styles.label}>
        {t(`${P}.FIELDS.${label}`)}
      </Typography>
      <TextField
        className={type === "textarea" ? styles.notesInput : styles.input}
        fullWidth
        select={type === "select"}
        multiline={type === "textarea"}
        variant="outlined"
        placeholder={placeholder(label, type)}
        value={form[key]}
        disabled={saving}
        InputProps={{ notched: false }}
        SelectProps={
          type === "select"
            ? {
                displayEmpty: true,
                IconComponent: DropdownIcon,
                renderValue: (value) => {
                  const selected = options.find(
                    (option) => option.value === value
                  );
                  return selected
                    ? optionLabel(selected)
                    : placeholder(label, "select");
                },
              }
            : undefined
        }
        onChange={(event) => setField(key, event.target.value)}
      >
        {type === "select"
          ? [
              <MenuItem key="empty" value="" disabled>
                {placeholder(label, "select")}
              </MenuItem>,
              ...options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {optionLabel(option)}
                </MenuItem>
              )),
            ]
          : undefined}
      </TextField>
    </Box>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>{t(`${P}.ADD_TITLE`)}</Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {ROWS.map((row, index) => (
            <Box
              key={index}
              className={row.length === 3 ? styles.gridThree : styles.grid}
            >
              {row.map(renderField)}
            </Box>
          ))}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={saving}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!isValid || saving}
            onClick={() => dispatch(registrationActions.addRegistration())}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddRegistrationModal;
